import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Proposal } from '@guabi/shared';
import { useCreateTask, useUpdateTask, useTasks } from '@/features/timeline/queries';
import { useGuests, useUpdateGuest } from '@/features/guests/queries';

/**
 * Changes the chat worked out, waiting to be made.
 *
 * **Confirming goes through the ordinary endpoints** — the same mutations the
 * buttons on the page use, with the same optimistic-concurrency checks. The AI
 * request never wrote anything, so there is no second write path to audit and no
 * way for a prompt to talk its way into one.
 *
 * Each is confirmed on its own. A single "do all three" button is how somebody
 * ends up agreeing to the one they did not read.
 */
export function Proposals({ wedId, proposals }: { wedId: string; proposals: Proposal[] }) {
  const { t } = useTranslation();
  const createTask = useCreateTask(wedId);
  const updateTask = useUpdateTask(wedId);
  const updateGuest = useUpdateGuest(wedId);
  const tasks = useTasks(wedId);
  const guests = useGuests(wedId);

  const [settled, setSettled] = useState<Record<number, 'done' | 'ignored'>>({});
  const [failed, setFailed] = useState<Record<number, string>>({});

  const confirm = (proposal: Proposal, i: number) => {
    const done = () => setSettled((prev) => ({ ...prev, [i]: 'done' }));
    const failedWith = (e: Error) => setFailed((prev) => ({ ...prev, [i]: e.message }));

    if (proposal.kind === 'add_task') {
      createTask.mutate(
        {
          title: proposal.title,
          category: proposal.category,
          ...(proposal.dueDate ? { dueDate: proposal.dueDate } : {}),
        },
        { onSuccess: done, onError: failedWith },
      );
      return;
    }

    if (proposal.kind === 'complete_task') {
      // `updatedAt` is read fresh rather than carried on the proposal: the chat
      // may have been open for minutes, and a stale one would 409 on a task the
      // other partner touched meanwhile.
      const current = tasks.data?.items.find((x) => x.taskId === proposal.taskId);
      if (!current) {
        setFailed((prev) => ({ ...prev, [i]: t('ask.proposalGone') }));
        return;
      }
      updateTask.mutate(
        { taskId: proposal.taskId, input: { done: true, updatedAt: current.updatedAt } },
        { onSuccess: done, onError: failedWith },
      );
      return;
    }

    const guest = (guests.data ?? []).find((g) => g.guestId === proposal.guestId);
    if (!guest) {
      setFailed((prev) => ({ ...prev, [i]: t('ask.proposalGone') }));
      return;
    }
    updateGuest.mutate(
      { guestId: proposal.guestId, input: { rsvp: proposal.rsvp, updatedAt: guest.updatedAt } },
      { onSuccess: done, onError: failedWith },
    );
  };

  return (
    <ul className="flex flex-col gap-2">
      {proposals.map((proposal, i) => (
        <li
          key={`${proposal.kind}-${proposal.summary}`}
          className="panel flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2"
        >
          <span className="min-w-0 flex-1 text-caption">{proposal.summary}</span>
          {settled[i] === 'done' ? (
            <span className="text-caption text-leaf">{t('ask.proposalDone')}</span>
          ) : settled[i] === 'ignored' ? (
            <span className="text-caption text-leaf-soft">{t('ask.proposalIgnored')}</span>
          ) : (
            <span className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={() => confirm(proposal, i)}
                className="min-h-tap text-caption text-brass-text underline underline-offset-4"
              >
                {t('ask.proposalConfirm')}
              </button>
              <button
                type="button"
                onClick={() => setSettled((prev) => ({ ...prev, [i]: 'ignored' }))}
                className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
              >
                {t('ask.proposalIgnore')}
              </button>
            </span>
          )}
          {failed[i] && (
            <span role="alert" className="w-full text-caption text-status-declinedFg">
              {failed[i]}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
